/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/auth";
import { toast } from "sonner";
import { Plus, Check, Sparkles, Star, Loader2, Search, ArrowRight, Crown } from "lucide-react";

const ngn = (n) => `₦${Number(n || 0).toLocaleString()}`;

export default function AddModulesPage() {
  const { subdomain } = useParams();
  const [data, setData] = useState(null);
  const [billing, setBilling] = useState("monthly");
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("All");
  const [busy, setBusy] = useState("");

  const load = async () => {
    try { setData((await api.get("/t/modules/catalog")).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load catalog"); }
  };
  useEffect(() => { load(); }, []);

  const request = async (module) => {
    setBusy(module);
    try {
      await api.post("/t/modules/request-add", { module, billing_cycle: billing });
      toast.success(`${module} requested — awaiting master-admin approval`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  if (!data) return <div className="p-12 flex items-center justify-center text-black/45"><Loader2 className="animate-spin" /></div>;

  const notIncluded = data.modules.filter((m) => !m.is_enabled);
  const enabled = data.modules.filter((m) => m.is_enabled);
  const groups = ["All", ...Array.from(new Set(notIncluded.map((m) => m.group)))];
  const filtered = notIncluded
    .filter((m) => group === "All" || m.group === group)
    .filter((m) => !q || m.module.toLowerCase().includes(q.toLowerCase()) || m.description.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (b.popular ? 1 : 0) - (a.popular ? 1 : 0) || (b.ai_recommended ? 1 : 0) - (a.ai_recommended ? 1 : 0));

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="add-modules-page">
      <header className="mb-6">
        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 text-[10px] font-extrabold uppercase tracking-wider mb-3">
          <Sparkles size={11} /> Upgrade your workspace
        </div>
        <h1 className="font-display font-extrabold text-3xl lg:text-4xl text-black">Upgrade</h1>
        <p className="text-sm text-black/55 mt-1.5 max-w-2xl">Every module that isn't part of your active plan lives here. Add what you need à la carte — no plan migration required. Save {data.yearly_discount_pct}% with yearly billing.</p>
      </header>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search modules…" data-testid="add-modules-search"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-black/15 text-sm focus:border-blue-500 outline-none" />
        </div>
        <div className="flex bg-black/5 rounded-lg p-1">
          <button onClick={() => setBilling("monthly")} data-testid="add-modules-billing-monthly"
            className={`px-3 py-1.5 text-xs font-bold rounded ${billing === "monthly" ? "bg-white shadow text-black" : "text-black/55"}`}>Monthly</button>
          <button onClick={() => setBilling("yearly")} data-testid="add-modules-billing-yearly"
            className={`px-3 py-1.5 text-xs font-bold rounded ${billing === "yearly" ? "bg-white shadow text-black" : "text-black/55"}`}>Yearly · -{data.yearly_discount_pct}%</button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-6">
        {groups.map((g) => (
          <button key={g} onClick={() => setGroup(g)} data-testid={`add-modules-group-${g}`}
            className={`px-3 py-1.5 text-xs font-bold rounded-md ${group === g ? "bg-black text-white" : "bg-black/5 text-black/65 hover:bg-black/10"}`}>{g}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-black/8 p-12 text-center" data-testid="add-modules-empty">
          <Crown size={32} className="mx-auto text-amber-500 mb-3" />
          <div className="font-display font-extrabold text-lg text-black">You've got everything!</div>
          <p className="text-sm text-black/55 mt-1">Your plan already includes every module in our catalog.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((m) => (
            <ModuleCard key={m.module} m={m} billing={billing} disc={data.yearly_discount_pct} onAdd={() => request(m.module)} busy={busy === m.module} />
          ))}
        </div>
      )}

      {enabled.length > 0 && (
        <div className="mt-12">
          <div className="font-display font-extrabold text-lg text-black mb-3">Already in your plan ({enabled.length})</div>
          <div className="flex flex-wrap gap-1.5">
            {enabled.map((m) => (
              <span key={m.module} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-bold" data-testid={`add-modules-enabled-${m.module}`}>
                <Check size={11} /> {m.module}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10 bg-gradient-to-br from-blue-50 to-violet-50 border border-blue-200 rounded-2xl p-6 flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="font-display font-extrabold text-lg text-black">Need more than just a few modules?</div>
          <p className="text-sm text-black/65 mt-1">Upgrade to a higher plan for bundled pricing and unlimited everything.</p>
        </div>
        <Link to={`/t/${subdomain}/billing`} data-testid="add-modules-upgrade-plan"
          className="px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-bold inline-flex items-center gap-2">
          See plans <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  );
}

function ModuleCard({ m, billing, disc, onAdd, busy }) {
  const price = billing === "yearly" ? m.yearly_price : m.monthly_price;
  const cycle = billing === "yearly" ? "/year" : "/month";
  return (
    <div className="relative bg-white rounded-2xl border border-black/8 p-5 hover:shadow-xl hover:border-blue-200 transition-all" data-testid={`module-card-${m.module}`}>
      {(m.popular || m.ai_recommended) && (
        <div className="absolute -top-2.5 left-5 flex gap-1.5">
          {m.popular && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400 text-amber-950 text-[10px] font-extrabold uppercase tracking-wide"><Star size={9} className="fill-current" /> Popular</span>}
          {m.ai_recommended && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-600 text-white text-[10px] font-extrabold uppercase tracking-wide"><Sparkles size={9} /> AI Pick</span>}
        </div>
      )}
      <div className="mb-3">
        <div className="text-[10px] uppercase font-bold tracking-wide text-black/45">{m.group}</div>
        <div className="font-display font-extrabold text-xl text-black mt-1">{m.module}</div>
        <p className="text-xs text-black/60 mt-1.5 leading-relaxed h-10">{m.description}</p>
      </div>
      <div className="border-t border-black/5 pt-4 flex items-end justify-between gap-3">
        <div>
          <div className="font-display font-extrabold text-2xl text-black">{price > 0 ? ngn(price) : "Custom"}<span className="text-xs font-normal text-black/55">{price > 0 ? cycle : ""}</span></div>
          {billing === "yearly" && m.yearly_price > 0 && <div className="text-[10px] text-emerald-700 font-bold mt-0.5">Save {disc}% vs monthly</div>}
        </div>
        <button onClick={onAdd} disabled={busy} data-testid={`module-add-${m.module}`}
          className="px-4 py-2.5 rounded-lg bg-[#1FA868] hover:bg-[#178755] text-white text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add module
        </button>
      </div>
    </div>
  );
}
